import { LinkType } from '@easeltv/analytics';
import { useCurrentRef } from '@easeltv/hooks';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { MenuLinkGroupId, Menus } from '../../../hooks/useGetMenu/useGetMenu';
import { useAnalytics } from '../../../providers/AnalyticsProvider/AnalyticsProvider';
import LinkAtom from '../../Atoms/LinkAtom';
import { getDestination } from '../../Atoms/LinkAtom/LinkAtom.helpers';
import ContextMenuBlock from '../../Blocks/ContextMenuBlock';
import MenuBlock from '../../Blocks/MenuBlock';
import SearchBlock from '../../Blocks/SearchBlock';
import {
    CtaMenuBlock,
    LogoWrapper,
    ShadowHeader,
    ShadowSticky,
    StickyCtaMenuBlock,
    StyledHeaderSection,
} from './HeaderSection.styled';

/* ---------------------------------- Types --------------------------------- */

export interface DumbHeaderSectionI {
    /** Controls the initial state of the Search field */
    initialSearchState: boolean;

    /** Flag for if the CW is in micro-site mode */
    isMicrosite: boolean;

    /** Logo element */
    logo: JSX.Element | null;

    /** Controls rendering of the Search input field */
    showSearchField: boolean;

    menuItems: Menus;
}

/* -------------------------------- Component ------------------------------- */

/**
 * DumbHeaderSection
 *
 * @param props DumbHeaderSectionI
 * @returns JSX.Element
 */
const DumbHeaderSection = ({
    initialSearchState,
    isMicrosite,
    showSearchField,
    logo,
    menuItems,
    children,
}: PropsWithChildren<DumbHeaderSectionI>) => {
    // Shadow ref is used to workout if the page is scrolled from the top of the page
    // When scrolled the brand logo in the header should shrink in size
    // We use a shadow as the real HeaderSection is fixed to the top of the viewport and scrolls with the page
    const shadowRef = useRef<HTMLHeadingElement>(null);

    // Determines whether the page is scrolled
    const [scrolled, setScrolled] = useState(false);

    const [searchOpen, setSearchOpen] = useState(initialSearchState);

    const { pageObject } = useAnalytics();

    const pageObjectRef = useCurrentRef(pageObject);

    const trackMenuLinkClicked =
        (menuSource: string) => (destination: LinkType) =>
            pageObjectRef.current &&
            window.analytics.trigger({
                type: 'menu.link.clicked',
                currentPage: pageObjectRef.current,
                destination,
                menuSource,
            });

    useEffect(() => {
        const { current } = shadowRef;

        // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
        const observer = new IntersectionObserver(
            ([header]) => {
                setScrolled(!header.isIntersecting);
            },
            // We use a threshold so scrolled is set to true after the header scrolls a little, rather that if one
            // pixel is out of view.
            { threshold: 0.75 },
        );

        // Start observing
        if (current) observer.observe(current);

        // Clean up
        return () => {
            if (current) observer.unobserve(current);
        };
    });

    return (
        <>
            <StyledHeaderSection scrolled={scrolled} searchOpen={searchOpen}>
                {logo && (
                    <LogoWrapper>
                        <Link to="/">{logo}</Link>
                    </LogoWrapper>
                )}
                {!isMicrosite && (
                    <>
                        <MenuBlock
                            items={menuItems.navigation}
                            trackMenuLinkClicked={trackMenuLinkClicked(
                                MenuLinkGroupId.navigation,
                            )}
                        />
                        <CtaMenuBlock>
                            {menuItems.cta.map((item) => (
                                <LinkAtom
                                    cta
                                    key={item.id}
                                    data={item}
                                    section="header"
                                    callback={() =>
                                        trackMenuLinkClicked(
                                            MenuLinkGroupId.cta,
                                        )(getDestination(item))
                                    }
                                />
                            ))}
                        </CtaMenuBlock>
                        {showSearchField && (
                            <SearchBlock
                                searchOpen={searchOpen}
                                setSearchOpen={setSearchOpen}
                            />
                        )}
                        <ContextMenuBlock
                            loggedInItems={menuItems.loggedIn}
                            loggedOutItems={menuItems.loggedOut}
                            trackMenuLinkClicked={trackMenuLinkClicked}
                        />
                    </>
                )}
            </StyledHeaderSection>
            <ShadowHeader ref={shadowRef} />
            {children}
            <ShadowSticky>
                {menuItems.cta.map((item) => (
                    <LinkAtom
                        cta
                        key={item.id}
                        data={item}
                        section="header"
                        callback={() =>
                            trackMenuLinkClicked('cta')(getDestination(item))
                        }
                    />
                ))}
            </ShadowSticky>
            {!isMicrosite && (
                <StickyCtaMenuBlock>
                    {menuItems.cta.map((item) => (
                        <LinkAtom
                            cta
                            key={item.id}
                            data={item}
                            section="header"
                            callback={() =>
                                trackMenuLinkClicked('cta')(
                                    getDestination(item),
                                )
                            }
                        />
                    ))}
                </StickyCtaMenuBlock>
            )}
        </>
    );
};

/* --------------------------------- Export --------------------------------- */

export default DumbHeaderSection;
